import { FOLDER_PERMISSIONS } from './permission_enum';

type PermissionConfig = {
  autoEnable: number[];
  autoEnableFor: number[];
  togglePermission?: (previousPermission: number) => number;
};

export const USE_TEMPLATE = FOLDER_PERMISSIONS.FOLDER_VIEW;
export const VIEW_ALL = FOLDER_PERMISSIONS.FOLDER_VIEW_ALL;
export const VIEW_MEMBERS_FORMS = FOLDER_PERMISSIONS.FOLDER_VIEW_MEMBERS_FORMS;

// Permission combinations
const COMBINATIONS = {
  VIEW_ALL: [USE_TEMPLATE + VIEW_ALL, USE_TEMPLATE + VIEW_MEMBERS_FORMS + VIEW_ALL],
  MEMBERS_FORMS: [USE_TEMPLATE + VIEW_MEMBERS_FORMS, USE_TEMPLATE + VIEW_ALL, VIEW_ALL]
};

const sum = (arr: number[]) => arr.reduce((runningTotal, val) => runningTotal + val, 0);

export const PERMISSION_CONFIGS: { [key: number]: PermissionConfig } = {
  [USE_TEMPLATE]: {
    autoEnable: [],
    autoEnableFor: [],
    togglePermission: (currentPermission: number) => {
      //If the current permission already has Use Template, then it must be a toggle off, so disable everything else since we need Use Template to enable view permissions
      if (currentPermission & USE_TEMPLATE) {
        return 0;
      }
      return USE_TEMPLATE;
    }
  },
  [VIEW_ALL]: {
    autoEnable: [VIEW_MEMBERS_FORMS, USE_TEMPLATE],
    autoEnableFor: COMBINATIONS.VIEW_ALL,
    togglePermission: (currentPermission: number) => {
      //If the current permission already has View All, then it must be a toggle off, so subtract View All from the value
      if (currentPermission & VIEW_ALL) {
        return currentPermission - VIEW_ALL;
      }
      return sum([USE_TEMPLATE, VIEW_MEMBERS_FORMS, VIEW_ALL]);
    }
  },
  [VIEW_MEMBERS_FORMS]: {
    autoEnable: [USE_TEMPLATE],
    autoEnableFor: COMBINATIONS.MEMBERS_FORMS,
    togglePermission: (currentPermission: number) => {
      //If the current permission already has View All + View Members Forms, then it must be a toggle off, so subtract View All and View Members Forms from the value
      if (currentPermission & VIEW_MEMBERS_FORMS && currentPermission & VIEW_ALL) {
        return currentPermission - VIEW_MEMBERS_FORMS - VIEW_ALL;
      }

      // For backward compatibility: handle cases where "view member's forms" is toggled off and current view all permission is 3 instead of 7, since "view member's forms" didn't exist previously.
      //3 = use template + view all
      //7 = use template + view members forms + view all
      if (currentPermission & VIEW_ALL) {
        return currentPermission - VIEW_ALL;
      }

      //If the current permission already has View Members Forms, then it must be a toggle off, so subtract View Members Forms from the value
      if (currentPermission & VIEW_MEMBERS_FORMS) {
        return currentPermission - VIEW_MEMBERS_FORMS;
      }

      //Can't have view members forms without use template, so if the current permission does not have use template, add that to the permission
      return currentPermission & USE_TEMPLATE ? currentPermission + VIEW_MEMBERS_FORMS : sum([USE_TEMPLATE, VIEW_MEMBERS_FORMS]);
    }
  }
};

/*
    Fallback for new permissions where bitwise checks may not suffice:
    e.g., 3 (view all) & 4 (view members' forms) === 4 is false,
    but we still need to enable 'view members' forms' when 'view all' is on.

    This function checks if enabling one permission should automatically
    enable others, handling cases where simple bitwise operations
    don't capture the desired behavior.
    */
export const doesPermissionAutoEnableOthers = (folderPermission: number, folderPermissionCombo: number) => {
  return PERMISSION_CONFIGS[folderPermission]?.autoEnableFor.includes(folderPermissionCombo);
};

export const USE_TEMPLATE_TOOLTIP = "Gives access to the corresponding folder's Templates.";
export const VIEW_MEMBERS_FORMS_TOOLTIP = "Gives access to any Forms created by this Group's members using the corresponding folder's Templates.";
export const VIEW_ALL_FORMS_TOOLTIP = "Gives access to all Forms created within your account using the corresponding folder's Templates.";

export const USE_TEMPLATE_TITLE = 'Use Templates';
export const VIEW_MEMBERS_FORMS_TITLE = "View member's Forms";
export const VIEW_ALL_FORMS_TITLE = 'View all Forms';
